/**
 * Conversation Detailed Report — data + filter options.
 *
 * Mirrors useAgentPerformanceReport: nothing is fetched until `run()` is
 * called with a validated filter set, so the page opens on its filter drawer
 * rather than firing an unbounded query nobody asked for.
 *
 * The column catalog is dynamic here — the API reports which additional
 * properties the tenant has defined — so `additionalColumns` is returned
 * alongside the rows for the page to build its catalog from.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import type { AdditionalPropertyColumn } from '../components/reports/conversation-detail/columns.js'

export interface DetailFilters {
  timezone: string
  startDate: string
  endDate: string
  locationIds: string[]
  branchIds: string[]
  departmentIds: string[]
  campaignIds: string[]
  teamIds: string[]
  statusIds: string[]
  stateIds: string[]
  typeIds: string[]
  categoryIds: string[]
  subCategoryIds: string[]
  priorityIds: string[]
  complexityIds: string[]
  urgencyIds: string[]
  impactIds: string[]
  agentIds: string[]
}

export interface FilterOption { label: string; value: string }
export interface TimezoneOption extends FilterOption { offset: string }

/**
 * The cascading lists carry their parent ids so the drawer can narrow
 * Location → Branch → Department and Campaign → Team → Agents without a
 * round trip per selection.
 */
export interface BranchOption extends FilterOption { locationId: string | null }
export interface DepartmentOption extends FilterOption {
  branchId: string | null
  locationId: string | null
}
export interface TeamOption extends FilterOption { campaignIds: string[] }
export interface AgentOption extends FilterOption {
  code: string | null
  teamIds: string[]
  campaignIds: string[]
}

export interface DetailFilterOptions {
  timezones: TimezoneOption[]
  locations: FilterOption[]
  branches: BranchOption[]
  departments: DepartmentOption[]
  campaigns: FilterOption[]
  teams: TeamOption[]
  statuses: FilterOption[]
  states: FilterOption[]
  types: FilterOption[]
  categories: FilterOption[]
  subCategories: FilterOption[]
  priorities: FilterOption[]
  complexities: FilterOption[]
  urgencies: FilterOption[]
  impacts: FilterOption[]
  agents: AgentOption[]
}

const EMPTY_OPTIONS: DetailFilterOptions = {
  timezones: [], locations: [], branches: [], departments: [], campaigns: [],
  teams: [], statuses: [], states: [], types: [], categories: [],
  subCategories: [], priorities: [], complexities: [], urgencies: [],
  impacts: [], agents: [],
}

/** Fetched once — these catalogs are tenant-wide, not campaign-scoped. */
export function useConversationDetailFilterOptions(): {
  options: DetailFilterOptions
  loading: boolean
} {
  const [options, setOptions] = useState<DetailFilterOptions>(EMPTY_OPTIONS)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<DetailFilterOptions>('/reports/conversations/detail/filters')
        if (!cancelled) setOptions({ ...EMPTY_OPTIONS, ...res })
      } catch {
        if (!cancelled) setOptions(EMPTY_OPTIONS)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { options, loading }
}

/** Every filter key that carries a list of ids, so the query builder can loop. */
const ID_KEYS = [
  'locationIds', 'branchIds', 'departmentIds', 'campaignIds', 'teamIds',
  'statusIds', 'stateIds', 'typeIds', 'categoryIds', 'subCategoryIds',
  'priorityIds', 'complexityIds', 'urgencyIds', 'impactIds', 'agentIds',
] as const

export function buildDetailParams(filters: DetailFilters): URLSearchParams {
  const p = new URLSearchParams({
    timezone: filters.timezone,
    startDate: filters.startDate,
    endDate: filters.endDate,
  })
  for (const key of ID_KEYS) {
    const values = filters[key]
    if (values.length > 0) p.set(key, values.join(','))
  }
  return p
}

interface UseDetailResult {
  rows: Record<string, unknown>[]
  additionalColumns: AdditionalPropertyColumn[]
  total: number
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: DetailFilters) => Promise<void>
  reset: () => void
}

export function useConversationDetailReport(): UseDetailResult {
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [additionalColumns, setAdditionalColumns] = useState<AdditionalPropertyColumn[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(async (filters: DetailFilters): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const params = buildDetailParams(filters)
      // Paging is client-side (see the page), so the whole bounded result set
      // is fetched once rather than re-queried per page.
      params.set('limit', '1000')
      const res = await api.get<{
        rows: Record<string, unknown>[]
        total: number
        additionalPropertiesColumns: AdditionalPropertyColumn[]
      }>(`/reports/conversations/detail?${params.toString()}`)
      setRows(res.rows ?? [])
      setAdditionalColumns(res.additionalPropertiesColumns ?? [])
      setTotal(res.total ?? 0)
      setHasRun(true)
    } catch (err) {
      setRows([])
      setAdditionalColumns([])
      setTotal(0)
      setHasRun(true)
      setError(err instanceof Error ? err.message : 'Could not load the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setAdditionalColumns([])
    setTotal(0)
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, additionalColumns, total, loading, error, hasRun, run, reset }
}
